/**
 * The categorical palette the panel's multi-series charts paint with.
 *
 * Every chart here is drawn by @ant-design/plots v2, which is G2 v5 under the
 * name. In v5 the channel that splits a chart into series (`seriesField`) and
 * the channel that colours it (`colorField`) are two different things — v1 had
 * one option doing both. The cafeteria charts carried the v1 spelling, so they
 * split into one line (or one bar group) per menu item and then painted every
 * one of them the same default hue: a legend of twenty names against twenty
 * identical swatches, which tells the reader nothing.
 *
 * COLOUR IS AN ENCODING, NOT DECORATION — hence the two rules below.
 *
 * 1. The hues are a fixed, ordered list, not a ramp and not generated. The
 *    order is what makes them readable: neighbouring slots are the pairs a
 *    reader compares (adjacent legend rows, adjacent bars in a group), so each
 *    consecutive pair has to survive red-green and blue-yellow colour-blindness
 *    simulation as well as normal vision. This ordering clears that gate on a
 *    white chart surface — worst adjacent pair ΔE 8.3 simulated / 19.8 normal
 *    (OKLab ×100, ≥8 and ≥15 the respective floors) — with every slot inside
 *    one lightness band so no series reads as "bigger" than another, none of
 *    them reading gray, and all twelve at 3:1 or better against the surface.
 *    Re-ordering the list is not cosmetic; it breaks that property. Slot 1 is
 *    the brand teal, a chroma-lifted step of `--primary` (`#0F7C8A` itself
 *    reads gray at chart scale).
 *
 * 2. A colour belongs to a menu item, not to its position in the current
 *    filter. `seriesSlots` takes the full item list, so deselecting "Tea" in
 *    the filter does not repaint everything that used to come after it — which
 *    is the difference between a chart you can learn and one you have to
 *    re-read every time you touch the controls.
 *
 * Past twelve series the hues repeat, and the repeat is carried by a second
 * channel instead of hue alone: `seriesDash` steps the line pattern once per
 * lap. Identity is never colour-alone in any case — the legend and the tooltip
 * both name the item.
 */

/** The twelve hues, in the order their separation was checked. */
export const CHART_SERIES_COLORS = [
  '#008C9E', // teal — brand primary, lifted to the chart chroma floor
  '#B08D3F', // gold
  '#6B4E9E', // purple
  '#7A8A00', // olive
  '#C94F82', // pink
  '#008300', // green
  '#9B5AC4', // violet
  '#96591F', // brown
  '#1C6FB8', // blue
  '#EB6834', // orange
  '#0E9F6E', // emerald
  '#C0392F' // red
];

/**
 * Line patterns, one per lap through the palette. Lap one is solid — the dashes
 * only start once a hue is being reused and needs a second channel to stay
 * distinct from its earlier self.
 */
const CHART_SERIES_DASHES: (number[] | undefined)[] = [undefined, [8, 4], [2, 3], [10, 3, 2, 3]];

/**
 * Assigns each series a stable slot number.
 *
 * `order` is the full set of possible series — the whole cafeteria menu, not
 * the filtered selection — which is what keeps an item's colour its own.
 * Anything in `series` that `order` does not know about (a menu item deleted
 * since the orders were placed, say) lands after the known slots rather than
 * stealing one.
 */
export function seriesSlots(series: string[], order: string[] = []): Record<string, number> {
  const slots: Record<string, number> = {};
  let unknown = order.length;

  series.forEach((name) => {
    const known = order.indexOf(name);
    slots[name] = known !== -1 ? known : unknown++;
  });

  return slots;
}

/** The hue for a slot. Wraps, and `seriesDash` marks the wrap. */
export const seriesColor = (slot: number) => CHART_SERIES_COLORS[slot % CHART_SERIES_COLORS.length];

/** The line pattern for a slot — `undefined` (solid) for the first twelve. */
export const seriesDash = (slot: number) =>
  CHART_SERIES_DASHES[Math.floor(slot / CHART_SERIES_COLORS.length) % CHART_SERIES_DASHES.length];
